import {
  AllReviews,
  BUTTON_SIZE,
  BUTTON_TYPES,
  NoReviews,
  ProductReviewGallerySlider,
  ProductReviewSummary,
} from '@src/quince-components/src'
import { If } from '@src/quince-components/src/atoms/conditionals'
import CreateReview from '@src/quince-components/src/atoms/reviews/createReview'
import { getIsBabyAndKidsProduct } from '@src/quince-components/src/atoms/reviews/createReview/reviewHelper'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import Product from '@src/quince-site-common/src/domains/Product'
import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import ProductReview from '@src/quince-site-common/src/domains/ProductReview'
import productHelper, { CUSTOM_FIELD_NAMES } from '@src/quince-site-common/src/helpers/productHelper'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import useMounted from '@src/quince-site-common/src/hooks/useMounted'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import reviewPopupStateManager from '@src/quince-site-common/src/redux/globalStateManager/ReviewPopupStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import productReviewService from '@src/quince-site-common/src/services/productReviewService'
import {
  IGetProductReviewsPayload,
  IGetProductReviewsResponse,
} from '@src/quince-site-common/src/services/productReviewService/productReviewService.types'
import { scrollRefIntoView, scrollToRef } from '@src/quince-site-common/src/utils/scrollToRef'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { useTranslation } from 'next-i18next'
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'

import useFilterState, { SORT_BY, SORT_BY_OPTIONS_MAP } from './hooks/useFilterState'
import { REVIEWS_SOURCES } from './productReviews.constant'
import styles from './productReviews.module.scss'
import { getReviewProductIds, getValidSummaryReviewOptions } from './utils'

// Note: Wrapper is created to enable progressive hydration internally, while keeping ability to scroll to ref working.
export const ProductReviewsWrapper = (props: ProductPageSectionProps | BundlePageSectionProps) => {
  const product = 'bundle' in props ? props.bundle : props.product
  const reviewSectionInFocus = useAppSelector<boolean>(pdpStateManager, pdpStateManager.getReviewSectionInFocus)

  const reviewSectionRef: MutableRefObject<HTMLDivElement | null> = useRef(null)
  useEffect(() => {
    if (reviewSectionInFocus) {
      scrollRefIntoView(reviewSectionRef)
      // Making it false so that from outside it can be toggled -- Having a delay as a hack as multiple copy are getting subscribed. // TODO: need to fix this.
      setTimeout(() => {
        pdpStateManager.setShowReviewSection({ reviewSectionInFocus: false })
      }, 1000)
    }
  }, [reviewSectionInFocus])

  return (
    <div ref={reviewSectionRef} className={styles.wrapper} id={`${product.id}_product_reviews_wrapper`}>
      <ProductReviews {...props} />
    </div>
  )
}

const ProductReviews = (props: ProductPageSectionProps | BundlePageSectionProps) => {
  const { t } = useTranslation('common')
  const product = 'bundle' in props ? props.bundle : props.product

  const firstProduct = product.type === PRODUCT_TYPES.BUNDLE ? product.customBundleProducts?.[0] : product

  const showLargeReviewForm = productHelper.getIsTruthyCustomField(
    firstProduct,
    CUSTOM_FIELD_NAMES.SHOW_LARGE_REVIEW_FORM,
  )

  const allReviewsSectionRef = useRef<HTMLInputElement>(null)
  const isMounted = useMounted()
  const [isCreateReviewPopUpOpen, setIsCreateReviewPopUpOpen] = useState(false)
  const { setHasPhoto, setSortBy, setPage, hasPhoto, page, sortBy } = useFilterState()

  const openCreateReviewPopUp = () => {
    setIsCreateReviewPopUpOpen(true)
  }

  const onCancelCreateReviewPopUp = () => {
    setIsCreateReviewPopUpOpen(false)
  }

  const {
    fetchData: fetchProductReviews,
    isLoading: isReviewsLoading,
    response: productReviewsResponse,
  } = useFetch<IGetProductReviewsResponse, [IGetProductReviewsPayload]>(
    productReviewService.getProductReviews.bind(this),
    {
      initialData: {
        reviews: [],
        total: product.productReviewSummary?.reviewCount ?? 0,
        hasNext: false,
      },
      onlyReturnLatestFetch: true,
    },
  )

  const [photoReviews, setPhotoReviews] = useState<ProductReview[]>([])

  useEffect(() => {
    const initiateReviewGalleryData = async () => {
      try {
        const response = await productReviewService.getProductReviews({
          ids: getReviewProductIds(product),
          sortBy: SORT_BY_OPTIONS_MAP[SORT_BY.HIGHEST_RATING].sortBy,
          sortOrder: SORT_BY_OPTIONS_MAP[SORT_BY.HIGHEST_RATING].sortOrder,
          isWithPhotos: true,
          pageSize: 1000,
        })
        setPhotoReviews(response.reviews)
      } catch (e) {
        captureException(e, {
          module: MODULES.REVIEW,
          feature: FEATURES.FETCH_REVIEW,
          extraInfo: {
            // @i18n-ignore
            capturedFrom: 'useEffect at ProductReviewsWrapper',
          },
        })
      }
    }
    initiateReviewGalleryData().catch(() => {
      /** no errors thrown */
    })
  }, [product])

  useEffect(() => {
    const sortByOption = SORT_BY_OPTIONS_MAP[sortBy]
    fetchProductReviews({
      ids: getReviewProductIds(product),
      sortBy: sortByOption.sortBy,
      sortOrder: sortByOption.sortOrder,
      isWithPhotos: hasPhoto,
      pageNumber: page - 1,
      pageSize: 4,
    }).catch(() => {
      /** no errors thrown */
    })
  }, [product.shopifyProductId, page, sortBy, hasPhoto])

  const onReviewImageClick = (reviewId: number, imageIndex: number) => {
    reviewPopupStateManager.showReviewPopUp({
      productReviews: photoReviews,
      reviewId: reviewId,
      imageIndex: imageIndex,
    })
  }

  const getIsVisibleTrackParams = useCallback(() => {
    return {
      // @i18n-ignore
      action: 'pdp_reviews',
      value: true,
      context: {
        productId: product.id,
        productName: product.productTypeName,
        productTypeName: product.title,
      },
    }
  }, [product.id, product.productTypeName, product.title])

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  const filterProps = useMemo(() => {
    return {
      sortBy: [
        { label: t('highest_rating'), value: SORT_BY.HIGHEST_RATING },
        { label: t('lowest_rating'), value: SORT_BY.LOWEST_RATING },
        { label: t('newest'), value: SORT_BY.NEWEST },
        { label: t('oldest'), value: SORT_BY.OLDEST },
      ],
      setSortBy,
      onSearchReview: (val: string) => {
        console.log('onsearch', val)
      },
      setHasPhoto,
      hasPhoto,
    }
  }, [setHasPhoto, setSortBy, hasPhoto])

  useEffect(() => {
    if (isMounted) scrollToRef(allReviewsSectionRef, 'auto')
  }, [page])

  const paginationProps = useMemo(
    () => ({
      totalPages: Math.ceil((productReviewsResponse?.total ?? 0) / 4),
      page,
      onChange: setPage,
      hideArrows: false,
      productProperties: getProductPropertiesForTracking(
        product as Product,
        (product as Product).variants?.[0]?.shopifyVariantId?.toString(),
      ),
    }),
    [page, product, productReviewsResponse],
  )

  if (!firstProduct) return null

  if (product.productReviewSummary?.reviewCount == 0) {
    return (
      <div className={styles.productNoReviews}>
        <NoReviews
          openCreateReviewPopUp={openCreateReviewPopUp}
          writeReviewButton={{
            type: BUTTON_TYPES.OUTLINED,
            size: BUTTON_SIZE.MEDIUM,
            children: t('write_a_review'),
          }}
          trackConfig={{
            trackGA: true,
            trackInternal: true,
            // @i18n-ignore
            category: 'reviews',
            // @i18n-ignore
            action: 'click_write_review',
            context: { product_id: product.shopifyProductId },
          }}
          message={t('we_want_to_know_what_you_think_be_the_first_to_leave_a_review')}
          hideWriteReviewButton={product.type === PRODUCT_TYPES.BUNDLE}
        />
        {
          <CreateReview
            show={isCreateReviewPopUpOpen}
            onCancel={onCancelCreateReviewPopUp}
            product={firstProduct}
            showLargeReviewForm={showLargeReviewForm}
            reviewSource={REVIEWS_SOURCES.PDP}
          />
        }
      </div>
    )
  }

  return (
    <div className={styles.productReviews} ref={trackIsVisibleRef}>
      <ProductReviewSummary
        openCreateReviewPopUp={openCreateReviewPopUp}
        className={styles.productReviewsSummary}
        avgRating={product.productReviewSummary?.averageRating ?? 0}
        product={product.type === PRODUCT_TYPES.BUNDLE ? undefined : product}
        writeReviewButton={{
          type: BUTTON_TYPES.OUTLINED,
          size: BUTTON_SIZE.MEDIUM,
          children: t('write_a_review'),
        }}
        trackConfig={{
          trackGA: true,
          trackInternal: true,
          // @i18n-ignore
          category: 'reviews',
          // @i18n-ignore
          action: 'click_write_review',
          context: { product_id: product.shopifyProductId },
        }}
        reviewsCount={product.productReviewSummary?.reviewCount ?? 0}
        hideWriteReviewButton={product.type === PRODUCT_TYPES.BUNDLE}
        ranges={getValidSummaryReviewOptions(
          product,
          product.productReviewSummary?.reviewOptions ?? [],
          showLargeReviewForm,
        ).map(ro => ({
          title: ro.name,
          fillPercentage: ro.rangePercent ?? 100,
          minLabel: ro.rangeOptions?.[0] ?? '',
          maxLabel: ro.rangeOptions?.slice(-1)[0] ?? '',
        }))}
      />
      <If condition={photoReviews.length > 0}>
        <div className={styles.productReviewsImageGallery}>
          <ProductReviewGallerySlider
            heading={t('gallery')}
            productReviews={photoReviews}
            onReviewImageClick={onReviewImageClick}
            productTitle={product.title}
          />
        </div>
      </If>
      <div ref={allReviewsSectionRef}>
        <AllReviews
          showReviewsLoader={isReviewsLoading}
          reviews={
            productReviewsResponse?.reviews.map(vr =>
              ProductReview.getProductReviewPropFromReview(vr, product.title, product.type === PRODUCT_TYPES.BUNDLE),
            ) ?? []
          }
          paginationProps={paginationProps}
          filterProps={filterProps}
          onReviewImageClick={onReviewImageClick}
          isKidsAndBabyProduct={getIsBabyAndKidsProduct(product)}
          productProperties={getProductPropertiesForTracking(
            product as Product,
            (product as Product).variants?.[0]?.shopifyVariantId?.toString(),
          )}
          variants={
            product.type === PRODUCT_TYPES.BUNDLE ? product.customBundleProducts?.[0]?.variants : product.variants
          }
        />
      </div>
      <CreateReview
        show={isCreateReviewPopUpOpen}
        onCancel={onCancelCreateReviewPopUp}
        product={firstProduct}
        showLargeReviewForm={showLargeReviewForm}
        reviewSource={REVIEWS_SOURCES.PDP}
      />
    </div>
  )
}
